import { ErrorResult } from "@/components/ui/ErrorResult";
import { SectionPrivateHeader } from "@/components/ui/SectionPrivateHeader";
import { formatTime } from "@/helpers/formatDate";
import useTableFilters from "@/hooks/table/useTableFilters";
import { useActivities } from "@/services/activities/queries";
import { Activity } from "@/types/activity";
import { Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

const getActivityColor = (a: string) => {
    if (a.includes("order")) return "blue";
    if (a.includes("payment")) return "green";
    if (a.includes("cancel")) return "red";
    return "default";
};

const getActivityLabelEs = (action: string) => {
    switch (action) {
        case "transferencia_de_mesa":
            return "Transferencia de mesa";
        case "table_opened":
            return "Mesa abierta";
        case "table_closed":
            return "Mesa cerrada";
        case "table_blocked":
            return "Mesa bloqueada";
        case "table_unblocked":
            return "Mesa desbloqueada";
        case "order_sent":
            return "Pedido enviado a cocina";
        case "order_ready":
            return "Pedido listo";
        case "order_adjusted":
            return "Comanda ajustada (Admin)";
        case "order_delivered":
            return "Pedido entregado";
        case "item_delivered":
            return "Producto entregado";
        case "payment_requested":
            return "Cobro solicitado";
        case "payment_received":
            return "Pago recibido";
        case "receipt_reprinted":
            return "Comprobante reimpreso";
        case "ticket_print_failed":
            return "Error al imprimir ticket";
        case "product_unavailable":
            return "Producto no disponible";
        case "discount_applied":
            return "Descuento aplicado";
        default:
            return String(action).replace(/_/g, " ");
    }
};

const columns: ColumnsType<Activity> = [
    {
        title: "Acción",
        dataIndex: "action",
        key: "action",
        render: (action: string) => (
            <Tag color={getActivityColor(action)}>
                {getActivityLabelEs(action).toUpperCase()}
            </Tag>
        )
    },
    {
        title: "Mesa",
        dataIndex: "table_number",
        key: "table_number",
        render: (table_number: number) => table_number ? `Mesa ${table_number}` : "-"
    },
    {
        title: "Detalles",
        dataIndex: "details",
        key: "details",
        render: (details: string, record: Activity) => {
            if (record.action === "payment_received" && record.table_number && !/mesa\s+\d+/i.test(details)) {
                return `Mesa ${record.table_number} · ${details}`;
            }
            return details;
        }
    },
    {
        title: "Responsable",
        dataIndex: "actor_role",
        key: "actor_role",
        render: (text: Activity['actor_role']) => {
            const role_map = {
                admin: { label: "Admin", color: "gold" },
                waiter: { label: "Mesero", color: "blue" },
                kitchen: { label: "Cocina", color: "green" },
                cashier: { label: "Cajero", color: "purple" },
                auditor: { label: "Auditor", color: "cyan" }
            }

            const role = role_map[text];
            if (!role) return null;

            return <Tag color={role.color}>{role.label}</Tag>;

        }
    },
    {
        title: "Fecha",
        dataIndex: "created_at",
        key: "created_at",
        render: (created_at: string) => formatTime(created_at)
    }
];

export const ActivitiesPrivate = () => {

    const { tableParams, tableKey, handleTableChange } = useTableFilters();

    const { data, isLoading, isError, error, isFetching, refetch } =
        useActivities(tableParams);

    if (isError) {
        return <ErrorResult error={error} refetch={refetch} />;
    }

    return (
        <>
            <SectionPrivateHeader
                title="Registro de actividad"
                existsButton={false}
            />

            <div className="ml-auto max-w-fit">
                <span className="text-xs px-2 py-1 rounded-full bg-[#070B0E] text-gray-300 border border-[#1A2328]">
                    {data?.total ?? 0} Eventos
                </span>
            </div>

            <Table<Activity>
                columns={columns}
                dataSource={data?.items}
                key={tableKey}
                pagination={{
                    current: tableParams?.pagination.current,
                    pageSize: tableParams?.pagination.pageSize,
                    total: data?.total,
                }}
                onChange={handleTableChange}
                loading={isLoading || isFetching}
                scroll={{ x: 1000 }}
                rowKey="id"
            />
        </>
    );
};